// Co-located helpers for the Material Verification (by production) screen.

/**
 * Groups dispensed containers into material cards keyed by
 * inhouse batch number + lot + part. Each card is verified and approved
 * independently so progress can be persisted card-by-card.
 */
export const buildMaterialCards = (weighingMaterials = []) => {
  const grouped = {};
  weighingMaterials.forEach((item) => {
    const key = `${item?.batch_detail?.inhouse_batch_number}|${item?.lot_no}|${item?.part_no}`;
    if (!grouped[key]) grouped[key] = [];
    grouped[key].push(item);
  });
  return Object.entries(grouped).map(([key, containers]) => ({
    key,
    containers
  }));
};

// Rounds to 2 decimals, half-up, on the DECIMAL value rather than the binary
// float — `Math.round(1.005 * 100) / 100` yields 1.00 because 1.005 is stored as
// 1.00499…, whereas the exponential-string shift below gives the 1.01 an
// operator expects. Used to round the reference gross weight before the
// tolerance band is derived from it.
const roundHalfUp2 = (value) => {
  const parsed = parseFloat(value);
  if (Number.isNaN(parsed)) return NaN;
  return Number(`${Math.round(Number(`${parsed}e2`))}e-2`);
};

/**
 * Tolerance band for a dispensed gross weight, or null when the weight or the
 * tolerance is unusable.
 *
 * The gross weight is first rounded to 2 decimals, then the tolerance
 * percentage is applied to THAT rounded reference — the limits themselves keep
 * full precision. For a 2.789 label at 0.10%:
 *   reference = 2.79,  0.10% of 2.79 = 0.00279
 *   lower = 2.78721,   upper = 2.79279
 */
export const getToleranceLimits = (expectedRaw, tolerance = null) => {
  const reference = roundHalfUp2(expectedRaw);
  const tolerancePct = parseFloat(tolerance?.tolerance_percentage) || 0;
  if (Number.isNaN(reference) || tolerancePct <= 0) return null;
  const allowed = (reference * tolerancePct) / 100;
  return {
    reference,
    allowed,
    lower: reference - allowed,
    upper: reference + allowed
  };
};

/**
 * Compares an entered gross weight against the dispensed gross weight using the
 * configured tolerance. Returns 'match' | 'within' | 'beyond' | null.
 */
export const getGrossStatus = (
  container,
  enteredWeights = {},
  tolerance = null
) => {
  const expected = parseFloat(container?.gross_weight);
  const entered = parseFloat(enteredWeights[container?.id]);
  if (Number.isNaN(expected) || Number.isNaN(entered)) return null;
  if (expected === entered) return 'match';
  const limits = getToleranceLimits(expected, tolerance);
  if (!limits) return 'beyond';
  return entered >= limits.lower && entered <= limits.upper
    ? 'within'
    : 'beyond';
};

/**
 * Renders a tolerance limit for display — up to 5 decimals, trailing zeros
 * trimmed (2.78721, 2.8).
 */
export const formatToleranceLimit = (value) =>
  Number.isFinite(value) ? String(parseFloat(value.toFixed(5))) : '';

export const isWeightMatched = (container, enteredWeights, tolerance) => {
  const status = getGrossStatus(container, enteredWeights, tolerance);
  return status === 'match' || status === 'within';
};

/**
 * Joins a balance name and dispense booth id into the `balanceName-boothId`
 * display label. Falls back to just the name when no booth id is present.
 * Balance name and dispense booth id are stored separately on the weighing
 * row — this label is only for display (dropdown, PDF).
 */
export const combineBalanceLabel = (balanceName, boothId) => {
  const name = balanceName || '';
  const id = boothId || '';
  return id ? `${name}-${id}` : name;
};

/**
 * Display label for a dispense booth record (`balanceName-dispenseBoothId`).
 */
export const formatBalanceName = (booth) =>
  combineBalanceLabel(booth?.balance_name, booth?.dispense_booth_id);

/**
 * Builds the "Product Batch Number" header value. Appends the dispense
 * request's process type and sub process to the batch number so the screen
 * shows the full dispense context, e.g. `BPA126055G - Granulation - Sifting`.
 * Each segment is included only when present, and the batch number falls back
 * to 'NA' when missing.
 */
export const formatProductBatchLabel = (prodDetails = {}) => {
  const batch_number = prodDetails?.product_batch_number || 'NA';
  const process_type =
    prodDetails?.prod_label_process_master?.process_label_name;
  const sub_process = prodDetails?.prod_label_sub_process?.sub_process_name;
  return [batch_number, process_type, sub_process].filter(Boolean).join(' - ');
};
